/**
 * Runnable sandbox scenarios — prove Lockbox + simulator without devices.
 *
 * Contract (unit-test shaped): each scenario builds an `EnforcementPlan`, runs `simulateEnforcement`,
 * then checks `expect(plan, sim)`. Eval-first scenarios use `evaluateLockbox`; synthetic rows test only
 * the simulator merge path. Assertions live next to `expect` so adding cases stays structured.
 */

import { evaluateLockbox } from "../domain/evaluate.js";
import { DEFAULT_RULEPACK } from "../domain/rulepackDefaults.js";
import { defaultClassification } from "../domain/types.js";
import { appendDecisionLog } from "./decisionLog.js";
import { simulateEnforcement } from "./sandboxEnforcer.js";

/** @typedef {import('../domain/types.js').EnforcementPlan} EnforcementPlan */
/** @typedef {import('./types.js').SimulatedEnforcementResult} SimulatedEnforcementResult */

/**
 * @typedef {Object} ScenarioRunResult
 * @property {boolean} ok
 * @property {string} message
 * @property {EnforcementPlan} [plan]
 * @property {SimulatedEnforcementResult} [sim]
 */

/**
 * @typedef {Object} LockboxScenario
 * @property {string} id
 * @property {string} title
 * @property {() => ScenarioRunResult} run
 */

/**
 * @param {EnforcementPlan} plan
 * @param {SimulatedEnforcementResult} sim
 * @param {(plan: EnforcementPlan, sim: SimulatedEnforcementResult) => boolean} expectFn
 * @param {string} passMessage
 */
function runScenario(plan, sim, expectFn, passMessage) {
  const ok = expectFn(plan, sim);
  return {
    ok,
    plan,
    sim,
    message: ok ? passMessage : JSON.stringify({ plan, sim }),
  };
}

/**
 * @param {import('../domain/types.js').LockboxAppEntity[]} inventory
 */
function labelResolverFromInventory(inventory) {
  return (/** @type {string} */ id) => inventory.find((a) => a.id === id)?.displayName ?? id;
}

/**
 * @param {string} id
 * @param {string} displayName
 * @param {import('../domain/types.js').BehavioralCategory} category
 */
function appRow(id, displayName, category) {
  return {
    id,
    displayName,
    classification: defaultClassification(category),
    nativeRef: null,
  };
}

/** @type {LockboxScenario[]} */
export const LOCKBOX_SANDBOX_SCENARIOS = [
  {
    id: "work_blocks_shortform_pair",
    title: "Work mode — two short-form apps restricted",
    run() {
      const apps = [appRow("lb_tiktok", "TikTok", "shortform_feed"), appRow("lb_ig", "Instagram", "shortform_feed")];
      const plan = evaluateLockbox(DEFAULT_RULEPACK, {
        now: new Date("2026-05-06T14:00:00"),
        activeModeId: "work",
        emergency: { active: false, until: null },
        apps,
      });
      const sim = simulateEnforcement(plan, {
        labelResolver: labelResolverFromInventory(apps),
        enforcementMode: "sandbox",
      });
      appendDecisionLog(plan, sim);
      return runScenario(
        plan,
        sim,
        (p, s) =>
          s.blocked.includes("TikTok") &&
          s.blocked.includes("Instagram") &&
          !s.allowed.includes("TikTok") &&
          s.triggeredRules.some((r) => r.includes("mode_strict")),
        "Both apps in simulated restricted set under work mode.",
      );
    },
  },
  {
    id: "work_allows_navigation",
    title: "Work mode — navigation allowed, short-form restricted",
    run() {
      const apps = [appRow("lb_tiktok", "TikTok", "shortform_feed"), appRow("lb_waze", "Waze", "navigation")];
      const plan = evaluateLockbox(DEFAULT_RULEPACK, {
        now: new Date("2026-05-06T14:00:00"),
        activeModeId: "work",
        emergency: { active: false, until: null },
        apps,
      });
      const sim = simulateEnforcement(plan, {
        labelResolver: labelResolverFromInventory(apps),
        enforcementMode: "sandbox",
      });
      appendDecisionLog(plan, sim);
      return runScenario(
        plan,
        sim,
        (p, s) =>
          s.blocked.includes("TikTok") && s.allowed.includes("Waze") && !s.allowed.includes("TikTok"),
        "TikTok restricted, Waze allowed (navigation template).",
      );
    },
  },
  {
    id: "sandbox_conflict_blocked_wins",
    title: "Sandbox conflict — same id in block + allow → blocked wins",
    run() {
      /** @type {EnforcementPlan} */
      const plan = {
        version: 1,
        computedAt: new Date("2026-05-06T21:00:00.000Z").toISOString(),
        activeModeId: "strict",
        blockAppIds: ["tiktok"],
        allowAppIds: ["tiktok"],
        restrictTargets: [{ type: "lockbox_app", id: "tiktok" }],
        allowTargets: [{ type: "lockbox_app", id: "tiktok" }],
        restrictBundleHints: [],
        allowBundleHints: [],
        scheduleHints: [],
        matchedRules: [{ ruleId: "fixture:conflict", reason: "Synthetic duplicate for sandbox merge test", appId: "tiktok" }],
      };
      const sim = simulateEnforcement(plan, {
        allowBlockConflictResolution: "blocked_wins",
        enforcementMode: "sandbox",
      });
      appendDecisionLog(plan, sim);
      return runScenario(
        plan,
        sim,
        (p, s) => s.blocked.includes("tiktok") && !s.allowed.includes("tiktok"),
        "Conflict resolver removed tiktok from allowed.",
      );
    },
  },
];

/**
 * @returns {{ scenarioId: string, ok: boolean, message: string }[]}
 */
export function runAllLockboxSandboxScenarios() {
  return LOCKBOX_SANDBOX_SCENARIOS.map((s) => {
    try {
      const r = s.run();
      return { scenarioId: s.id, ok: r.ok, message: `${s.title}: ${r.message}` };
    } catch (e) {
      return { scenarioId: s.id, ok: false, message: `${s.title}: ${e?.message || String(e)}` };
    }
  });
}

/**
 * @param {string} scenarioId
 */
export function runLockboxSandboxScenario(scenarioId) {
  const s = LOCKBOX_SANDBOX_SCENARIOS.find((x) => x.id === scenarioId);
  if (!s) return { ok: false, message: `Unknown scenario: ${scenarioId}` };
  return s.run();
}
